import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RecipientCard from './components/RecipientCard';
import RecipientTable from './components/RecipientTable';
import RecipientFilters from './components/RecipientFilters';
import VerificationSummary from './components/VerificationSummary';
import AddRecipientModal from './components/AddRecipientModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import RecipientDetailsModal from './components/RecipientDetailsModal';

const RecipientsManagement = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    role: 'customer'
  });

  // Mock recipients data
  const [recipients, setRecipients] = useState([
  {
    id: '1',
    name: 'Emma Thompson',
    relationship: 'family',
    bankName: 'Barclays Bank UK',
    iban: 'GB29 NWBK 6016 1331 9268 19',
    swiftCode: 'BARCGB22',
    country: 'United Kingdom',
    countryFlag: "https://images.unsplash.com/photo-1628006383628-d2cd5fe968e7",
    supportedCurrencies: ['GBP', 'EUR'],
    verificationStatus: 'verified',
    lastUsed: '2024-11-17T14:30:00Z'
  },
  {
    id: '2',
    name: 'Hans Mueller',
    relationship: 'business',
    bankName: 'Deutsche Bank AG',
    iban: 'DE89 3704 0044 0532 0130 00',
    swiftCode: 'DEUTDEFF',
    country: 'Germany',
    countryFlag: "https://images.unsplash.com/photo-1712752498506-544ce44f45cf",
    supportedCurrencies: ['EUR', 'USD'],
    verificationStatus: 'pending',
    lastUsed: '2024-11-15T09:15:00Z'
  },
  {
    id: '3',
    name: 'Marie Dubois',
    relationship: 'friend',
    bankName: 'BNP Paribas',
    iban: 'FR14 2004 1010 0505 0001 3M02 606',
    swiftCode: 'BNPAFRPP',
    country: 'France',
    countryFlag: "https://images.unsplash.com/photo-1701170910876-44931d0bbd54",
    supportedCurrencies: ['EUR'],
    verificationStatus: 'verified',
    lastUsed: '2024-11-10T16:45:00Z'
  },
  {
    id: '4',
    name: 'Carlos Rodriguez',
    relationship: 'family',
    bankName: 'Banco Santander',
    iban: 'ES91 2100 0418 4502 0005 1332',
    swiftCode: 'BSCHESMM',
    country: 'Spain',
    countryFlag: "https://images.unsplash.com/photo-1732551760143-ef1d1df6cbc4",
    supportedCurrencies: ['EUR'],
    verificationStatus: 'failed',
    lastUsed: '2024-11-08T11:20:00Z'
  },
  {
    id: '5',
    name: 'Marco Rossi',
    relationship: 'business',
    bankName: 'UniCredit Bank',
    iban: 'IT60 X054 2811 1010 0000 0123 456',
    swiftCode: 'UNCRITMM',
    country: 'Italy',
    countryFlag: "https://images.unsplash.com/photo-1733924307395-9bfc5b46fb89",
    supportedCurrencies: ['EUR'],
    verificationStatus: 'verified',
    lastUsed: '2024-11-05T13:30:00Z'
  },
  {
    id: '6',
    name: 'Anna van der Berg',
    relationship: 'contractor',
    bankName: 'ING Bank N.V.',
    iban: 'NL91 ABNA 0417 1643 00',
    swiftCode: 'INGBNL2A',
    country: 'Netherlands',
    countryFlag: 'https://flagcdn.com/24x18/nl.png',
    supportedCurrencies: ['EUR'],
    verificationStatus: 'pending',
    lastUsed: '2024-11-03T10:15:00Z'
  }]
  );

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter recipients based on search and filters
  const filteredRecipients = recipients?.filter((recipient) => {
    const matchesSearch = !searchTerm ||
    recipient?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    recipient?.bankName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    recipient?.iban?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesCountry = !selectedCountry || recipient?.country === selectedCountry;
    const matchesStatus = !selectedStatus || recipient?.verificationStatus === selectedStatus;

    return matchesSearch && matchesCountry && matchesStatus;
  });

  // Handle logout
  const handleLogout = () => {
    navigate('/login');
  };

  // Handle add recipient
  const handleAddRecipient = (newRecipient) => {
    setRecipients((prev) => [newRecipient, ...prev]);
  };

  // Handle edit recipient
  const handleEditRecipient = (recipient) => {
    setSelectedRecipient(recipient);
    setIsAddModalOpen(true);
  };

  // Handle delete recipient
  const handleDeleteRecipient = (recipient) => {
    setSelectedRecipient(recipient);
    setIsDeleteModalOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    setIsDeleting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setRecipients((prev) => prev?.filter((r) => r?.id !== selectedRecipient?.id));
      setIsDeleteModalOpen(false);
      setSelectedRecipient(null);
    } catch (error) {
      console.error('Error deleting recipient:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle send money
  const handleSendMoney = (recipient) => {
    navigate('/send-money-flow', { state: { selectedRecipient: recipient } });
  };

  // Handle view details
  const handleViewDetails = (recipient) => {
    setSelectedRecipient(recipient);
    setIsDetailsModalOpen(true);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCountry('');
    setSelectedStatus('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Recipients Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage your trusted recipients and their banking details
            </p>
          </div>
          
          <div className="mt-4 sm:mt-0">
            <Button
              variant="default"
              onClick={() => setIsAddModalOpen(true)}
              iconName="Plus"
              iconPosition="left"
              iconSize={20}>

              Add New Recipient
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Filters */}
            <RecipientFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              selectedCountry={selectedCountry}
              onCountryChange={setSelectedCountry}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onClearFilters={handleClearFilters} />


            {/* Results Summary */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredRecipients?.length} of {recipients?.length} recipients
              </p>
              
              {filteredRecipients?.length === 0 && searchTerm &&
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAddModalOpen(true)}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}>

                  Add "{searchTerm}" as recipient
                </Button>
              }
            </div>

            {/* Recipients Display */}
            {filteredRecipients?.length > 0 ?
            viewMode === 'cards' ?
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredRecipients?.map((recipient) =>
              <RecipientCard
                key={recipient?.id}
                recipient={recipient}
                onEdit={handleEditRecipient}
                onDelete={handleDeleteRecipient}
                onSendMoney={handleSendMoney}
                onViewDetails={handleViewDetails} />

              )}
                </div> :

            <RecipientTable
              recipients={filteredRecipients}
              onEdit={handleEditRecipient}
              onDelete={handleDeleteRecipient}
              onSendMoney={handleSendMoney}
              onViewDetails={handleViewDetails} /> :



            <div className="bg-card border border-border rounded-lg p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {searchTerm || selectedCountry || selectedStatus ? 'No recipients found' : 'No recipients yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm || selectedCountry || selectedStatus ?
                'Try adjusting your search criteria or filters' : 'Add your first recipient to start sending money internationally'
                }
                </p>
                <Button
                variant="default"
                onClick={() => setIsAddModalOpen(true)}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}>

                  Add New Recipient
                </Button>
              </div>
            }
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <VerificationSummary recipients={recipients} />
          </div>
        </div>
      </div>
      {/* Modals */}
      <AddRecipientModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedRecipient(null);
        }}
        onAdd={handleAddRecipient} />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedRecipient(null);
        }}
        onConfirm={confirmDelete}
        recipient={selectedRecipient}
        isDeleting={isDeleting} />

      <RecipientDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedRecipient(null);
        }}
        recipient={selectedRecipient}
        onEdit={handleEditRecipient}
        onSendMoney={handleSendMoney} />

    </div>);

};

export default RecipientsManagement;